import { useState } from "react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import Seo from "../components/Seo";
import { usePWAInstall } from "../lib/pwa";
import { API } from "../lib/api";

// Once the extension is listed on the Chrome Web Store, drop the public listing
// URL here for the true 1-click "add to Chrome" flow. Until then the page falls
// back to the developer (load-unpacked) install of the bundled zip.
const CHROME_STORE_URL = "";

export default function Install() {
  const { installed, promptInstall } = usePWAInstall();
  const [hint, setHint] = useState(false);
  const [steps, setSteps] = useState(false);

  const isChromium =
    typeof navigator !== "undefined" &&
    /Chrome|Chromium|Edg/.test(navigator.userAgent) &&
    !/Mobile|Android|iPhone|iPad/.test(navigator.userAgent);

  const onInstallApp = async () => {
    const ok = await promptInstall();
    if (!ok) setHint(true);
  };

  return (
    <div data-testid="install-page">
      <Seo
        title="Install the desktop app"
        description="Install TerminalValue as a desktop app and add the command bar to launch your stack from anywhere."
        path="/install"
      />
      <Header />
      <main className="max-w-2xl mx-auto px-4 py-8">
        <h1 className="text-xl font-bold border-b border-black pb-2">{">"}_ install</h1>
        <p className="text-[13px] text-[#555] mt-3 leading-snug">
          Get the desktop app. Two quick steps.
        </p>

        {/* Step 1 — the app */}
        <div className="border-2 border-black p-4 mt-5 bg-[#FBF8EE]" data-testid="install-step-app">
          <div className="flex items-baseline gap-3">
            <span className="text-[#999] font-bold">1.</span>
            <div className="flex-1">
              <div className="font-bold mb-1">install the desktop app</div>
              <p className="text-[12px] text-[#666] leading-snug mb-3">
                Opens TerminalValue in its own app window.
              </p>
              {installed ? (
                <div className="font-bold text-[13px]" data-testid="install-app-done">
                  app installed ✓
                </div>
              ) : (
                <button
                  type="button"
                  onClick={onInstallApp}
                  className="border border-black bg-black text-white px-3 py-2 text-[12px] font-bold inline-block hover:bg-[#222] transition-colors"
                  data-testid="install-app-btn"
                >
                  [ install the app → ]
                </button>
              )}
              {hint && !installed && (
                <p className="text-[11px] text-[#888] mt-2" data-testid="install-app-hint">
                  No one-click install? Use the install icon in your browser&apos;s address
                  bar — or on iPhone, <span className="font-bold">Share → Add to Home Screen</span>.
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Step 2 — the command bar / extension */}
        <div className="border-2 border-black p-4 mt-4 bg-[#FBF8EE]" data-testid="install-step-ext">
          <div className="flex items-baseline gap-3">
            <span className="text-[#999] font-bold">2.</span>
            <div className="flex-1">
              <div className="font-bold mb-1">add the command bar</div>
              <p className="text-[12px] text-[#666] leading-snug mb-3">
                Open your tools from anywhere with one click.
              </p>

              {CHROME_STORE_URL ? (
                <a
                  href={CHROME_STORE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="border border-black bg-black text-white px-3 py-2 text-[12px] font-bold inline-block hover:bg-[#222] transition-colors no-underline"
                  data-testid="install-ext-store-btn"
                >
                  [ add to Chrome → ]
                </a>
              ) : (
                <div className="flex flex-wrap items-center gap-2">
                  <a
                    href={`${API}/extension/download`}
                    className="border border-black bg-black text-white px-3 py-2 text-[12px] font-bold inline-block hover:bg-[#222] transition-colors no-underline"
                    data-testid="install-ext-download-btn"
                    onClick={() => setSteps(true)}
                  >
                    [ download the extension → ]
                  </a>
                  <button
                    type="button"
                    onClick={() => setSteps((s) => !s)}
                    className="text-[12px] text-[#888] hover:text-black hover:underline"
                    data-testid="install-ext-steps-toggle"
                  >
                    {steps ? "hide" : "how?"}
                  </button>
                </div>
              )}

              {!isChromium && (
                <p className="text-[11px] text-[#888] mt-2" data-testid="install-ext-chrome-note">
                  Use Chrome or Edge on desktop for this part.
                </p>
              )}

              {!CHROME_STORE_URL && steps && (
                <ol
                  className="text-[11px] text-[#444] mt-3 pl-4 list-decimal space-y-1 leading-snug"
                  data-testid="install-ext-steps"
                >
                  <li>Unzip the download.</li>
                  <li>
                    Go to <span className="font-mono">chrome://extensions</span> and turn on{" "}
                    <span className="font-bold">Developer mode</span>.
                  </li>
                  <li>
                    Click <span className="font-bold">Load unpacked</span> and pick the folder.
                  </li>
                </ol>
              )}
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
